import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { adminContent } from "@/i18n/admin";
import { useLang } from "@/i18n/language";
import { supabase } from "@/integrations/supabase/client";
import { formatDate } from "@/lib/utils";

const inputClass =
  "w-full rounded-lg border border-border bg-background/60 px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary";
const cardClass = "rounded-xl border border-border bg-surface p-6";
const btnPrimary =
  "rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50";
const labelClass = "block text-xs font-semibold uppercase tracking-wide text-muted-foreground";

type Author = { name: string; country: string };

type PublicationRow = {
  id: string;
  title: string;
  journal_name: string;
  year: number | null;
  is_indexed: boolean;
  has_quartile: boolean;
  description: string | null;
  doi: string | null;
  link_url: string | null;
  authors: unknown;
  sort_order: number;
  created_at: string;
};

type FormState = {
  title: string;
  journal_name: string;
  year: string;
  is_indexed: boolean;
  has_quartile: boolean;
  description: string;
  doi: string;
  link_url: string;
  authors: Author[];
};

const emptyForm: FormState = {
  title: "",
  journal_name: "",
  year: "",
  is_indexed: false,
  has_quartile: false,
  description: "",
  doi: "",
  link_url: "",
  authors: [{ name: "", country: "" }],
};

function parseAuthors(value: unknown): Author[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((a) => {
      const row = (a ?? {}) as Record<string, unknown>;
      return {
        name: typeof row['name'] === "string" ? row['name'] : "",
        country: typeof row['country'] === "string" ? row['country'] : "",
      };
    })
    .filter((a) => a.name.trim().length > 0);
}

function toForm(row: PublicationRow): FormState {
  const authors = parseAuthors(row.authors);
  return {
    title: row.title,
    journal_name: row.journal_name,
    year: row.year ? String(row.year) : "",
    is_indexed: row.is_indexed,
    has_quartile: row.has_quartile,
    description: row.description ?? "",
    doi: row.doi ?? "",
    link_url: row.link_url ?? "",
    authors: authors.length ? authors : [{ name: "", country: "" }],
  };
}

type SwitchProps = {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  activeLabel: string;
  inactiveLabel: string;
};

function Switch({ label, checked, onChange, activeLabel, inactiveLabel }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/40 px-4 py-3 text-sm"
    >
      <span className="text-foreground">{label}</span>
      <span className="flex items-center gap-2">
        <span
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            checked ? "bg-primary" : "bg-muted"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-background shadow-sm transition-transform ${
              checked ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </span>
        <span className="w-8 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {checked ? activeLabel : inactiveLabel}
        </span>
      </span>
    </button>
  );
}

export function PublicationsPanel() {
  const { lang } = useLang();
  const t = adminContent[lang].publications;
  const queryClient = useQueryClient();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const pubsQuery = useQuery({
    queryKey: ["admin-publications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("publications")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as PublicationRow[];
    },
  });

  const rows = pubsQuery.data ?? [];

  function reset() {
    setEditingId(null);
    setForm(emptyForm);
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setAuthor(index: number, key: keyof Author, value: string) {
    setForm((prev) => ({
      ...prev,
      authors: prev.authors.map((a, i) => (i === index ? { ...a, [key]: value } : a)),
    }));
  }

  async function save() {
    const title = form.title.trim();
    const journal = form.journal_name.trim();
    if (title.length < 3 || journal.length < 2) {
      toast.error(t.invalid);
      return;
    }
    const yearValue = form.year.trim() ? Number(form.year.trim()) : null;
    if (yearValue !== null && (!Number.isInteger(yearValue) || yearValue < 1900 || yearValue > 2100)) {
      toast.error(t.invalidYear);
      return;
    }
    const authors = form.authors
      .map((a) => ({ name: a.name.trim(), country: a.country.trim() }))
      .filter((a) => a.name.length > 0);

    const payload = {
      title,
      journal_name: journal,
      year: yearValue,
      is_indexed: form.is_indexed,
      has_quartile: form.has_quartile,
      description: form.description.trim() || null,
      doi: form.doi.trim() || null,
      link_url: form.link_url.trim() || null,
      authors,
    };

    setSaving(true);
    const { error } = editingId
      ? await supabase.from("publications").update(payload).eq("id", editingId)
      : await supabase.from("publications").insert(payload);
    setSaving(false);

    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(editingId ? t.updated : t.created);
    reset();
    queryClient.invalidateQueries({ queryKey: ["admin-publications"] });
  }

  async function remove(id: string) {
    if (!window.confirm(t.confirmDelete)) return;
    const { error } = await supabase.from("publications").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (editingId === id) reset();
    toast.success(t.deleted);
    queryClient.invalidateQueries({ queryKey: ["admin-publications"] });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
      <div className={cardClass}>
        <h2 className="text-lg font-semibold">{editingId ? t.editHeading : t.heading}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.lead}</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className={labelClass}>{t.title}</label>
            <input
              className={`${inputClass} mt-2`}
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder={t.titlePlaceholder}
            />
          </div>

          <div>
            <label className={labelClass}>{t.journal}</label>
            <input
              className={`${inputClass} mt-2`}
              value={form.journal_name}
              onChange={(e) => setField("journal_name", e.target.value)}
              placeholder={t.journalPlaceholder}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>{t.year}</label>
              <input
                className={`${inputClass} mt-2`}
                value={form.year}
                inputMode="numeric"
                onChange={(e) => setField("year", e.target.value)}
                placeholder="2026"
              />
            </div>
            <div>
              <label className={labelClass}>{t.doi}</label>
              <input
                className={`${inputClass} mt-2`}
                value={form.doi}
                onChange={(e) => setField("doi", e.target.value)}
                placeholder={t.doiPlaceholder}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>{t.link}</label>
            <input
              className={`${inputClass} mt-2`}
              value={form.link_url}
              onChange={(e) => setField("link_url", e.target.value)}
              placeholder={t.linkPlaceholder}
            />
          </div>

          <div className="grid gap-3">
            <Switch
              label={t.indexed}
              checked={form.is_indexed}
              onChange={(checked) => setField("is_indexed", checked)}
              activeLabel={t.yes}
              inactiveLabel={t.no}
            />
            <Switch
              label={t.quartile}
              checked={form.has_quartile}
              onChange={(checked) => setField("has_quartile", checked)}
              activeLabel={t.yes}
              inactiveLabel={t.no}
            />
          </div>

          <div>
            <label className={labelClass}>{t.description}</label>
            <textarea
              className={`${inputClass} mt-2 min-h-[96px]`}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder={t.descriptionPlaceholder}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className={labelClass}>{t.authors}</label>
              <button
                type="button"
                className="text-xs font-semibold text-primary"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    authors: [...prev.authors, { name: "", country: "" }],
                  }))
                }
              >
                + {t.addAuthor}
              </button>
            </div>
            <div className="mt-3 space-y-3">
              {form.authors.map((author, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <input
                    className={inputClass}
                    value={author.name}
                    onChange={(e) => setAuthor(index, "name", e.target.value)}
                    placeholder={t.authorName}
                  />
                  <input
                    className={inputClass}
                    value={author.country}
                    onChange={(e) => setAuthor(index, "country", e.target.value)}
                    placeholder={t.authorCountry}
                  />
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        authors:
                          prev.authors.length > 1
                            ? prev.authors.filter((_, i) => i !== index)
                            : [{ name: "", country: "" }],
                      }))
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button className={btnPrimary} onClick={save} disabled={saving}>
              {editingId ? t.saveChanges : t.create}
            </button>
            {editingId ? (
              <button className={btnGhost} onClick={reset}>
                {t.cancel}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.empty}</p>
        ) : (
          rows.map((row) => {
            const authors = parseAuthors(row.authors);
            return (
              <article key={row.id} className={cardClass}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold">{row.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {row.journal_name}
                      {row.year ? ` · ${row.year}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className={btnGhost}
                      onClick={() => {
                        setEditingId(row.id);
                        setForm(toForm(row));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {t.edit}
                    </button>
                    <button className={btnGhost} onClick={() => remove(row.id)}>
                      {t.delete}
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wide">
                  <span className="rounded-full border border-border px-3 py-1 text-muted-foreground">
                    {t.indexed}: {row.is_indexed ? t.yes : t.no}
                  </span>
                  <span className="rounded-full border border-border px-3 py-1 text-muted-foreground">
                    {t.quartile}: {row.has_quartile ? t.yes : t.no}
                  </span>
                </div>

                {row.description ? (
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {row.description}
                  </p>
                ) : null}

                <div className="mt-4 text-sm">
                  <p className={labelClass}>{t.authors}</p>
                  {authors.length === 0 ? (
                    <p className="mt-1 text-muted-foreground">{t.noAuthors}</p>
                  ) : (
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      {authors.map((a, i) => (
                        <li key={i}>
                          {a.name}
                          {a.country ? ` — ${a.country}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  {row.doi ? (
                    <span>
                      <span className={labelClass}>{t.doi}:</span>{" "}
                      <span className="text-foreground/80">{row.doi}</span>
                    </span>
                  ) : null}
                  {row.link_url ? (
                    <a
                      href={row.link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-primary"
                    >
                      {t.openLink} →
                    </a>
                  ) : null}
                  <span>{formatDate(row.created_at)}</span>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}
